import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import {
  REQUIRED_TERMS,
  TERM_LABELS,
  ACK_REASON_MIN,
  termsUrl,
  confirmationUrl,
  versionsUrl,
  ackUrl,
  changedTerms,
  formValue,
  validateProvenance,
  buildConfirmationPayload,
  buildVersionPayload,
  describeRefusal,
  holdReasonText,
  readinessText,
  provenanceView,
  confirmable,
  statedProblems,
  buildAckPayload,
  bumpsVersion,
  ackRefusalText,
} from "./fundTermsModel";

/**
 * FundTerms - the fund's subscription terms, where each value came from, and
 * the D7 terms review hold (R4, Friday release 2026-10-09).
 *
 * WHY IT EXISTS. A fund transacts (orders, details, payment, settlement) only
 * while admin_configs 'funds.require_confirmed_terms' is satisfied: every
 * required term CONFIRMED from a named source. Prod 809/810 hold terms seeded
 * before that existed, so they stay closed until the issuer's confirmation is
 * recorded here. Recording values EQUAL to the stored ones keeps the version
 * (signed orders such as 1761 then settle). The reviewer TYPES each value as
 * the source states it (nothing is pre-selected or pre-filled), so the API's
 * "stated value must equal the stored one" check compares the source with the
 * record. A change to a term the investor's agreement states (Notes currency,
 * nominal per Note, issue date, subscription agreement) is a NEW VERSION,
 * which holds every signed unpaid order of the old version until a compliance
 * seat acknowledges the review of its signed snapshot; a change to the
 * minimums or the countersigner only is recorded on the current version and
 * holds nothing. The acknowledgement is offered exactly when the server says
 * this viewer may give it (liveOrders[].ackable: signed before the change, not
 * the viewer's own order), and names the two versions reviewed.
 *
 * ACCESS. The API decides: requireRole('AD', 'CCO') on every call. The route
 * is RoleGuard allow={['AD','CCO']} so nobody else is offered a screen that
 * would 403; a 403 is still rendered as the server's refusal.
 *
 * Inline forms, not SweetAlert: Swal is mocked in every suite here, so its
 * inputs could never be driven by a test. Every success re-reads the terms
 * rather than trusting the response.
 */

const API = process.env.REACT_APP_API;
const headers = () => ({ "x-access-token": localStorage.getItem("accessToken") });
const today = () => new Date().toISOString().slice(0, 10);

const statusStyle = (counted) => ({
  fontWeight: 600,
  color: counted ? "var(--pos-700)" : "var(--neg-700)",
});

// Confirmed or not is the SERVER's verdict (terms.unconfirmed), never the
// entry's own status: an entry claiming CONFIRMED without an actor does not count.
function ProvenanceCell({ field, entry, unconfirmed }) {
  const view = provenanceView(field, entry, unconfirmed);
  return (
    <span>
      <span style={statusStyle(view.counted)} data-counted={view.counted ? "true" : "false"}>{view.label}</span>
      {entry && (
        <span style={{ color: "var(--tx-3)" }}>
          {" "}{entry.source}{entry.date ? ` (${entry.date})` : ""}{entry.actor != null ? ` · by ${entry.actor}` : ""}
        </span>
      )}
    </span>
  );
}

/**
 * One screen per fund (Codex P1-4, W3b). The route's :campaignId changes
 * without a remount (both routes render this element), so the screen is keyed
 * by it: opening another fund mounts a NEW screen, and no edit, source,
 * review note or flash of the previous fund survives into it.
 */
export default function FundTerms() {
  const { campaignId } = useParams();
  return <FundTermsScreen key={campaignId || ""} campaignId={campaignId} />;
}

function FundTermsScreen({ campaignId }) {
  const navigate = useNavigate();
  const [lookup, setLookup] = useState(campaignId || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);

  // Confirmation form: the selected terms and, per term, the value the source states.
  const [selected, setSelected] = useState([]);
  const [stated, setStated] = useState({});
  const [confSource, setConfSource] = useState("");
  const [confDate, setConfDate] = useState("");
  const [confError, setConfError] = useState(null);

  // New version form
  const [edited, setEdited] = useState({});
  const [verSource, setVerSource] = useState("");
  const [verDate, setVerDate] = useState("");
  const [verReason, setVerReason] = useState("");
  const [verAck, setVerAck] = useState(false);
  const [verError, setVerError] = useState(null);

  // Acknowledgement forms, per order
  const [ackReasons, setAckReasons] = useState({});
  const [ackErrors, setAckErrors] = useState({});

  // Only the LATEST load of a screen that is still mounted may set state: an
  // answer that arrives after another load started, or after the route moved
  // to another fund (this screen unmounted), is dropped.
  const alive = useRef(true);
  const loadSeq = useRef(0);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (!campaignId) return;
    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    const latest = () => alive.current && seq === loadSeq.current;
    setError(null);
    try {
      const res = await axios.get(termsUrl(API, campaignId), { headers: headers() });
      if (!latest()) return;
      const d = res.data && res.data.data;
      // An answer for another fund than the route names is never shown, so
      // nothing on screen can be submitted against the wrong fund.
      if (!d || String(d.campaignId) !== String(campaignId)) {
        setData(null);
        setError(`The terms received are not those of fund #${campaignId}. Nothing can be changed here; reload the page.`);
        return;
      }
      setData(d);
      const values = (d && d.terms && d.terms.values) || {};
      const nextEdited = {};
      for (const f of REQUIRED_TERMS) nextEdited[f] = formValue(values[f]);
      setEdited(nextEdited);
      // Nothing pre-selected, nothing pre-filled: the reviewer selects what
      // the source confirms and types what it states.
      setSelected([]);
      setStated({});
    } catch (err) {
      if (!latest()) return;
      setData(null);
      setError(describeRefusal(err, "Could not load the fund terms."));
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  // Every mutation acts only on the fund the route names, once ITS terms are
  // the ones loaded: buttons disabled and handlers refusing otherwise.
  const forThisFund = !!data && String(data.campaignId) === String(campaignId);
  const terms = data && data.terms;
  const values = (terms && terms.values) || {};
  const changed = terms ? changedTerms(values, edited) : [];
  const bump = bumpsVersion(changed);
  const currentVersion = terms ? terms.termsVersion : null;

  const submitConfirmation = async (e) => {
    e.preventDefault();
    if (!forThisFund) return undefined;
    setConfError(null);
    const toConfirm = selected.filter((f) => confirmable(f, values[f]));
    if (!toConfirm.length) return setConfError("Select at least one term to confirm.");
    const { missing, differing } = statedProblems(values, stated, toConfirm);
    const names = (list) => list.map((f) => TERM_LABELS[f]).join(", ");
    if (missing.length) return setConfError(`Enter the value the source states for: ${names(missing)}.`);
    if (differing.length) {
      return setConfError(`The source states another value for: ${names(differing)}. A confirmation records the stored values only; a different value is a terms change (below), a new terms version when the agreement states it.`);
    }
    const invalid = validateProvenance({ source: confSource, date: confDate }, today());
    if (invalid) return setConfError(invalid);
    setBusy(true);
    try {
      await axios.put(confirmationUrl(API, campaignId), buildConfirmationPayload(stated, toConfirm, confSource, confDate), { headers: headers() });
      if (!alive.current) return undefined;
      setFlash({ tone: "info", text: `Recorded as CONFIRMED: ${toConfirm.map((f) => TERM_LABELS[f]).join(", ")}. The terms version is unchanged.` });
      setConfSource("");
      setConfDate("");
      setStated({});
      await load();
    } catch (err) {
      if (alive.current) setConfError(describeRefusal(err, "Could not record the confirmation."));
    } finally {
      if (alive.current) setBusy(false);
    }
    return undefined;
  };

  const submitVersion = async (e) => {
    e.preventDefault();
    if (!forThisFund) return undefined;
    setVerError(null);
    if (!changed.length) return setVerError("No term changes value. Use the confirmation to record the current terms.");
    const invalid = validateProvenance({ source: verSource, date: verDate }, today());
    if (invalid) return setVerError(invalid);
    // Only a new VERSION holds orders, so only it needs the acknowledgement.
    if (bump && !verAck) return setVerError("Confirm that signed unpaid orders of this fund will be held for a compliance review.");
    setBusy(true);
    try {
      const res = await axios.post(versionsUrl(API, campaignId), buildVersionPayload(values, edited, verSource, verDate, verReason), { headers: headers() });
      if (!alive.current) return undefined;
      const out = (res.data && res.data.data) || {};
      const changedNames = (out.changed || changed).map((f) => TERM_LABELS[f] || f).join(", ");
      if (out.versionBumped === false) {
        setFlash({ tone: "info", text: `Terms updated on version ${out.termsVersion} (changed: ${changedNames}). No order is held.` });
      } else {
        const held = out.heldOrders || [];
        const superseded = out.signedOnSupersededTerms || [];
        const remint = out.ordersToRemint || [];
        const open = out.openEnvelopes || [];
        setFlash({
          tone: "warning",
          text: `Terms version ${out.termsVersion} created (changed: ${changedNames}). `
            + `${held.length ? `Held for review: ${held.map((id) => `#${id}`).join(", ")}. ` : "No signed order to hold. "}`
            + `${superseded.length ? `Signed on superseded terms, a review cannot release them (new signature required: re-issue the agreement): ${superseded.map((id) => `#${id}`).join(", ")}. ` : ""}`
            + `${remint.length ? `Unsigned orders to re-confirm: ${remint.map((id) => `#${id}`).join(", ")}. ` : ""}`
            + `${open.length ? `Envelopes open on superseded terms (to void and re-mint): ${open.map((x) => `#${x.investmentId} (${x.status})`).join(", ")}.` : ""}`,
        });
      }
      setVerSource("");
      setVerDate("");
      setVerReason("");
      setVerAck(false);
      await load();
    } catch (err) {
      if (alive.current) setVerError(describeRefusal(err, "Could not create the new terms version."));
    } finally {
      if (alive.current) setBusy(false);
    }
    return undefined;
  };

  const submitAck = async (order) => {
    if (!forThisFund) return;
    const reason = (ackReasons[order.investmentId] || "").trim();
    if (reason.length < ACK_REASON_MIN) {
      setAckErrors((m) => ({ ...m, [order.investmentId]: `A reason of at least ${ACK_REASON_MIN} characters is required.` }));
      return;
    }
    setAckErrors((m) => ({ ...m, [order.investmentId]: null }));
    setBusy(true);
    try {
      await axios.post(ackUrl(API, order.investmentId), buildAckPayload(order, currentVersion, reason), { headers: headers() });
      if (!alive.current) return;
      setFlash({ tone: "info", text: `Review of order #${order.investmentId} acknowledged: it proceeds on its signed terms.` });
      setAckReasons((m) => ({ ...m, [order.investmentId]: "" }));
      await load();
    } catch (err) {
      if (alive.current) setAckErrors((m) => ({ ...m, [order.investmentId]: describeRefusal(err, "Could not acknowledge the review.") }));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const toggle = (f) => setSelected((s) => (s.includes(f) ? s.filter((x) => x !== f) : [...s, f]));

  return (
    <PageShell>
      <PageHead
        title="Fund terms"
        subtitle={terms ? <span>{data.campaignName || `Fund #${data.campaignId}`} · terms version <span className="mono">{terms.termsVersion}</span></span> : null}
      />

      <form
        onSubmit={(e) => { e.preventDefault(); if (String(lookup).trim()) navigate(`/fund-terms/${String(lookup).trim()}`); }}
        style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 12 }}
      >
        <Field label="Fund campaign id" name="lookup" value={lookup} onChange={(e) => setLookup(e.target.value)} />
        <Btn type="submit">Open</Btn>
      </form>

      {flash && <Alert tone={flash.tone}>{flash.text}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      {data && (
        <>
          <section className="card" style={{ marginBottom: 12 }}>
            <div className="card__body">
              <p data-testid="readiness">{readinessText(data.readiness)}</p>
              <p style={{ color: "var(--tx-3)" }}>
                funds.require_confirmed_terms: <strong>{data.requireConfirmedTerms ? "on (every term must be CONFIRMED)" : "off (SEEDED terms accepted)"}</strong>
              </p>
            </div>
          </section>

          {terms && (
            <section className="card" style={{ marginBottom: 12 }}>
              <div className="card__body card__body--flush">
                <table className="tbl">
                  <thead>
                    <tr><th>Term</th><th>Value</th><th>Provenance</th></tr>
                  </thead>
                  <tbody>
                    {REQUIRED_TERMS.map((f) => (
                      <tr key={f} data-term={f}>
                        <td>{TERM_LABELS[f]}</td>
                        <td className="mono">{formValue(values[f]) || "(none)"}</td>
                        <td><ProvenanceCell field={f} entry={terms.provenance && terms.provenance[f]} unconfirmed={terms.unconfirmed} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {terms && (
            <section className="card" style={{ marginBottom: 12 }}>
              <div className="card__body">
                <h3>Record a confirmation of the current terms</h3>
                <p style={{ color: "var(--tx-3)" }}>
                  Select each term the source confirms and type the value it states (leave it blank only where the source states no separate minimum). Every stated value must equal the stored one: the selected terms are then recorded as CONFIRMED. The version does not change, so no order is held.
                </p>
                <form onSubmit={submitConfirmation} aria-label="Record a confirmation">
                  {REQUIRED_TERMS.map((f) => {
                    const can = confirmable(f, values[f]);
                    const on = can && selected.includes(f);
                    return (
                      <div key={f}>
                        <label style={{ display: "block" }}>
                          <input type="checkbox" name={`confirm-${f}`} checked={on} disabled={!can} onChange={() => toggle(f)} />{" "}
                          {TERM_LABELS[f]}
                          {!can && <span style={{ color: "var(--tx-3)" }}>: (none): set it with a terms change</span>}
                        </label>
                        {on && (
                          <Field
                            label={`${TERM_LABELS[f]}, as the source states it`}
                            name={`confirm-value-${f}`}
                            value={stated[f] === undefined ? "" : stated[f]}
                            onChange={(e) => { const v = e.target.value; setStated((m) => ({ ...m, [f]: v })); }}
                          />
                        )}
                      </div>
                    );
                  })}
                  <Field label="Source" name="confirm-source" value={confSource} onChange={(e) => setConfSource(e.target.value)} />
                  <Field label="Date of the source" name="confirm-date" type="date" value={confDate} onChange={(e) => setConfDate(e.target.value)} />
                  {confError && <Alert tone="danger">{confError}</Alert>}
                  <Btn type="submit" disabled={busy || !forThisFund}>Record confirmation</Btn>
                </form>
              </div>
            </section>
          )}

          {terms && (
            <section className="card" style={{ marginBottom: 12 }}>
              <div className="card__body">
                <h3>Change the terms</h3>
                <p style={{ color: "var(--tx-3)" }}>
                  Change only what the source amends. Only the CHANGED terms are sent, and they are recorded as CONFIRMED from this source; unchanged terms keep their provenance (record a confirmation for them separately). A change to the Notes currency, the nominal per Note, the issue date or the subscription agreement (the terms the investor&apos;s agreement states) creates terms version {(terms.termsVersion || 0) + 1}: signed but unpaid subscriptions keep their signed snapshot and are HELD (no payment, no settlement) until the AD or the CCO acknowledges their review; unsigned orders must save their subscriber details again under the new version, and an agreement still open on the old terms has to be re-issued. A change to the minimums or the countersigner only is recorded on terms version {terms.termsVersion} and holds nothing.
                </p>
                <form onSubmit={submitVersion} aria-label="New terms version">
                  {REQUIRED_TERMS.map((f) => (
                    <Field
                      key={f}
                      label={`${TERM_LABELS[f]}${changed.includes(f) ? " (changed)" : ""}`}
                      name={`version-${f}`}
                      type={f === "issue_date" ? "date" : "text"}
                      hint={f === "agreement_key" ? "One of AEP, LCP, REGP, MAPLE." : undefined}
                      value={edited[f] === undefined ? "" : edited[f]}
                      onChange={(e) => { const v = e.target.value; setEdited((m) => ({ ...m, [f]: v })); }}
                    />
                  ))}
                  <Field label="Source" name="version-source" value={verSource} onChange={(e) => setVerSource(e.target.value)} />
                  <Field label="Date of the source" name="version-date" type="date" value={verDate} onChange={(e) => setVerDate(e.target.value)} />
                  <Field label="Reason (optional)" name="version-reason" as="textarea" value={verReason} onChange={(e) => setVerReason(e.target.value)} />
                  {bump && (
                    <label style={{ display: "block" }}>
                      <input type="checkbox" name="version-ack" checked={verAck} onChange={(e) => setVerAck(e.target.checked)} />{" "}
                      I understand signed unpaid orders of this fund will be held for a compliance review.
                    </label>
                  )}
                  <p data-testid="version-changes" style={{ color: "var(--tx-3)" }}>
                    {changed.length
                      ? `Will be sent: ${changed.map((f) => TERM_LABELS[f]).join(", ")}. ${bump
                        ? `This creates terms version ${(terms.termsVersion || 0) + 1} and holds signed unpaid orders.`
                        : `These are recorded on terms version ${terms.termsVersion}; no order is held.`}`
                      : "No term changes value yet."}
                  </p>
                  {verError && <Alert tone="danger">{verError}</Alert>}
                  <Btn type="submit" disabled={busy || !changed.length || !forThisFund}>{changed.length && !bump ? "Record terms change" : "Create terms version"}</Btn>
                </form>
              </div>
            </section>
          )}

          {terms && (
            <section className="card">
              <div className="card__body card__body--flush">
                <table className="tbl" aria-label="Live orders">
                  <thead>
                    <tr><th>Order</th><th>Investor</th><th className="r">Amount</th><th>Signed</th><th>Version</th><th>Hold</th><th></th></tr>
                  </thead>
                  <tbody>
                    {(data.liveOrders || []).length === 0 && (
                      <tr><td colSpan={7}>No live orders on this fund.</td></tr>
                    )}
                    {(data.liveOrders || []).map((o) => (
                      <tr key={o.investmentId} data-order={o.investmentId}>
                        <td className="mono">#{o.investmentId}</td>
                        <td className="mono">#{o.userId}</td>
                        <td className="r">{o.amount} {o.currency}</td>
                        <td>{o.signed ? "yes" : "no"}{o.envelopeStatus ? <span style={{ color: "var(--tx-3)" }}> ({o.envelopeStatus})</span> : null}</td>
                        <td className="mono">{o.termsVersion === null ? "none" : o.termsVersion}</td>
                        <td>{o.held ? holdReasonText(o.holdReason) : "not held"}{o.reviewAckAt ? ` (acknowledged by #${o.reviewAckBy})` : ""}</td>
                        <td>
                          {o.held && !o.ackable && ackRefusalText(o.ackRefusal) && (
                            <span style={{ color: "var(--tx-3)" }}>{ackRefusalText(o.ackRefusal)}</span>
                          )}
                          {/* Offered exactly when the API would accept it from this viewer. */}
                          {o.held && o.ackable === true && (
                            <div>
                              <Field
                                label="Review note"
                                name={`ack-${o.investmentId}`}
                                as="textarea"
                                value={ackReasons[o.investmentId] || ""}
                                onChange={(e) => { const v = e.target.value; setAckReasons((m) => ({ ...m, [o.investmentId]: v })); }}
                              />
                              {ackErrors[o.investmentId] && <Alert tone="danger">{ackErrors[o.investmentId]}</Alert>}
                              <Btn onClick={() => submitAck(o)} disabled={busy || !forThisFund}>Acknowledge review</Btn>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </PageShell>
  );
}
